import * as React from 'react';

import {
  AsyncBoundary,
  Button,
  Col,
  ContentCard,
  ContentSection,
  Row,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideSave, LucideTruck } from 'lucide-react';

import { FreightFormSection } from './FreightFormSection';

import {
  locationFreightPricingCreateAtom,
  locationFreightPricingUpdateAtom,
} from '~/modules/trade/_graphql/mutationLocationFreightPricing';
import { locationFreightPricingQueryAtom } from '~/modules/trade/_graphql/queryLocationFreightPricing';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type LocationFreightPreferencesProps = {
  locationType?: 'Facility' | 'AccountAddress';
  locationId: string;
  disabled?: boolean;
};

type FreightFormState = {
  supplierFreightEnabled: boolean;
  supplierRateFormula: string;
  supplierChargebackFormula: string;
  supplierFuelSurchargeFormula: string;
  supplierFuelSurchargeMode: string;
  consumerFreightEnabled: boolean;
  consumerRateFormula: string;
  consumerChargebackFormula: string;
  consumerFuelSurchargeFormula: string;
  consumerFuelSurchargeMode: string;
  consumerNegativeCharge: boolean;
};

const emptyFormState: FreightFormState = {
  supplierFreightEnabled: false,
  supplierRateFormula: '',
  supplierChargebackFormula: '',
  supplierFuelSurchargeFormula: '',
  supplierFuelSurchargeMode: '',
  consumerFreightEnabled: false,
  consumerRateFormula: '',
  consumerChargebackFormula: '',
  consumerFuelSurchargeFormula: '',
  consumerFuelSurchargeMode: '',
  consumerNegativeCharge: false,
};

export const LocationFreightPreferences = ({
  locationType = 'Facility',
  locationId,
  disabled = false,
}: LocationFreightPreferencesProps) => {
  const t = useTranslations();
  const queryAtom = React.useMemo(
    () =>
      locationFreightPricingQueryAtom(() => ({
        variables: { locationId },
        skip: !locationId,
      })),
    [locationId],
  );

  const {
    data: pricingConfig,
    isLoading,
    error,
    refetch,
  } = useAtomValue(queryAtom);

  const createPricing = useSetAtom(
    locationFreightPricingCreateAtom({
      onSuccess: () => {
        refetch?.();
      },
    }),
  );
  const updatePricing = useSetAtom(
    locationFreightPricingUpdateAtom({
      onSuccess: () => {
        refetch?.();
      },
    }),
  );

  const [form, setForm] = React.useState<FreightFormState>(emptyFormState);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (pricingConfig) {
      setForm({
        supplierFreightEnabled: pricingConfig.supplierFreightEnabled ?? false,
        supplierRateFormula: pricingConfig.supplierRateFormula ?? '',
        supplierChargebackFormula:
          pricingConfig.supplierChargebackFormula ?? '',
        supplierFuelSurchargeFormula:
          pricingConfig.supplierFuelSurchargeFormula ?? '',
        supplierFuelSurchargeMode:
          pricingConfig.supplierFuelSurchargeMode ?? '',
        consumerFreightEnabled: pricingConfig.consumerFreightEnabled ?? false,
        consumerRateFormula: pricingConfig.consumerRateFormula ?? '',
        consumerChargebackFormula:
          pricingConfig.consumerChargebackFormula ?? '',
        consumerFuelSurchargeFormula:
          pricingConfig.consumerFuelSurchargeFormula ?? '',
        consumerFuelSurchargeMode:
          pricingConfig.consumerFuelSurchargeMode ?? '',
        consumerNegativeCharge: pricingConfig.consumerNegativeCharge ?? false,
      });
    }
  }, [pricingConfig]);

  const handleFieldChange = React.useCallback(
    <K extends keyof FreightFormState>(field: K) =>
      (value: FreightFormState[K]) => {
        setForm((prev) => ({ ...prev, [field]: value }));
      },
    [],
  );

  const handleSave = React.useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = {
        supplierFreightEnabled: form.supplierFreightEnabled,
        supplierRateFormula: form.supplierRateFormula || null,
        supplierChargebackFormula: form.supplierChargebackFormula || null,
        supplierFuelSurchargeFormula: form.supplierFuelSurchargeFormula || null,
        supplierFuelSurchargeMode: form.supplierFuelSurchargeMode || null,
        consumerFreightEnabled: form.consumerFreightEnabled,
        consumerRateFormula: form.consumerRateFormula || null,
        consumerChargebackFormula: form.consumerChargebackFormula || null,
        consumerFuelSurchargeFormula: form.consumerFuelSurchargeFormula || null,
        consumerFuelSurchargeMode: form.consumerFuelSurchargeMode || null,
        consumerNegativeCharge: form.consumerNegativeCharge,
      };

      if (pricingConfig?.id) {
        await updatePricing({ id: pricingConfig.id, ...payload });
      } else {
        await createPricing({ locationType, locationId, ...payload });
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    form,
    pricingConfig?.id,
    locationType,
    locationId,
    createPricing,
    updatePricing,
  ]);

  return (
    <ContentCard className="@LocationFreightPreferences">
      <ContentSection
        variant="panel"
        title={
          <Row gap2 className="items-center">
            <LucideTruck size={18} />
            <span>{t.locationFreightPreferences.title}</span>
          </Row>
        }
      >
        <AsyncBoundary loading={isLoading} error={error}>
          <Col gap4>
            <FreightFormSection
              title={t.locationFreightPreferences.supplierFreight}
              enabled={form.supplierFreightEnabled}
              onEnabledChange={handleFieldChange('supplierFreightEnabled')}
              rateFormula={form.supplierRateFormula}
              onRateFormulaChange={handleFieldChange('supplierRateFormula')}
              chargebackFormula={form.supplierChargebackFormula}
              onChargebackFormulaChange={handleFieldChange(
                'supplierChargebackFormula',
              )}
              fuelSurchargeFormula={form.supplierFuelSurchargeFormula}
              onFuelSurchargeFormulaChange={handleFieldChange(
                'supplierFuelSurchargeFormula',
              )}
              fuelSurchargeMode={form.supplierFuelSurchargeMode}
              onFuelSurchargeModeChange={handleFieldChange(
                'supplierFuelSurchargeMode',
              )}
              disabled={disabled}
            />

            <div className="border-t border-neutral/10" />

            <FreightFormSection
              title={t.locationFreightPreferences.consumerFreight}
              enabled={form.consumerFreightEnabled}
              onEnabledChange={handleFieldChange('consumerFreightEnabled')}
              rateFormula={form.consumerRateFormula}
              onRateFormulaChange={handleFieldChange('consumerRateFormula')}
              chargebackFormula={form.consumerChargebackFormula}
              onChargebackFormulaChange={handleFieldChange(
                'consumerChargebackFormula',
              )}
              fuelSurchargeFormula={form.consumerFuelSurchargeFormula}
              onFuelSurchargeFormulaChange={handleFieldChange(
                'consumerFuelSurchargeFormula',
              )}
              fuelSurchargeMode={form.consumerFuelSurchargeMode}
              onFuelSurchargeModeChange={handleFieldChange(
                'consumerFuelSurchargeMode',
              )}
              negativeCharge={form.consumerNegativeCharge}
              onNegativeChargeChange={handleFieldChange(
                'consumerNegativeCharge',
              )}
              showNegativeCharge
              disabled={disabled}
            />

            {!disabled && (
              <Row className="justify-end">
                <Button
                  variant="solid"
                  onPress={handleSave}
                  isDisabled={isSaving}
                >
                  <Row gap2 className="items-center">
                    <LucideSave size={16} />
                    <span>
                      {pricingConfig?.id
                        ? t.locationFreightPreferences.buttons.update
                        : t.locationFreightPreferences.buttons.save}
                    </span>
                  </Row>
                </Button>
              </Row>
            )}
          </Col>
        </AsyncBoundary>
      </ContentSection>
    </ContentCard>
  );
};
